"use client";

import { useRouter } from "next/navigation";
import { PropsWithChildren, useEffect, useRef } from "react";
import { supabaseClient } from "../supabase/supabaseClient";
import { ROOT_DOMAIN } from "../utils/navigation-utils";
import { isVercelPreviewDeploy } from "../utils/orgHostRouting";
import * as Sentry from "@sentry/nextjs";

function isRedirectDomain() {
  if (typeof window === "undefined") return false;
  // Vercel preview deployments are not custom/redirect domains — auth must run normally
  if (isVercelPreviewDeploy()) return false;
  const host = window.location.host;
  return host !== ROOT_DOMAIN && !host.endsWith(`.${ROOT_DOMAIN}`);
}

// Fetches nothing: the server already resolved the user. Refreshes only when the
// user id changes; INITIAL_SESSION and same-user token events render the same.
export default function AuthProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const lastUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isRedirectDomain()) return;

    const { data } = supabaseClient.auth.onAuthStateChange((event, session) => {
      const currentUserId = session?.user.id ?? null;

      Sentry.setUser(currentUserId ? { id: currentUserId } : null);

      if (event === "INITIAL_SESSION") {
        lastUserId.current = currentUserId;
        return;
      }

      const previousUserId = lastUserId.current;
      lastUserId.current = currentUserId;
      if (previousUserId === currentUserId) return;

      router.refresh();
    });

    return () => {
      data?.subscription?.unsubscribe();
    };
  }, [router]);

  return <>{children}</>;
}
